interface SimpleMenuAimOptions {
  trigger: HTMLElement;
  menu: HTMLElement;
  activate: () => void;
  deactivate: () => void;
  tolerance?: number;
  delay?: number;
}

interface MousePosition {
  x: number;
  y: number;
}

class SimpleMenuAim {
  private options: SimpleMenuAimOptions;
  private isActive = false;
  private mouseLocs: MousePosition[] = [];
  private timeoutId: number | null = null;
  private boundMouseMove: (e: MouseEvent) => void;
  private boundTriggerEnter: () => void;
  private boundTriggerLeave: () => void;
  private boundMenuEnter: () => void;
  private boundMenuLeave: () => void;

  constructor(options: SimpleMenuAimOptions) {
    this.options = {
      tolerance: 75,
      delay: 300,
      ...options,
    };

    // Bind methods to preserve 'this' context
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundTriggerEnter = this.onTriggerEnter.bind(this);
    this.boundTriggerLeave = this.onTriggerLeave.bind(this);
    this.boundMenuEnter = this.onMenuEnter.bind(this);
    this.boundMenuLeave = this.onMenuLeave.bind(this);

    this.init();
  }

  private init(): void {
    // Track mouse movement globally
    document.addEventListener('mousemove', this.boundMouseMove);

    // Trigger events
    this.options.trigger.addEventListener('mouseenter', this.boundTriggerEnter);
    this.options.trigger.addEventListener('mouseleave', this.boundTriggerLeave);

    // Menu events
    this.options.menu.addEventListener('mouseenter', this.boundMenuEnter);
    this.options.menu.addEventListener('mouseleave', this.boundMenuLeave);
  }

  private onMouseMove(e: MouseEvent): void {
    this.mouseLocs.push({ x: e.pageX, y: e.pageY });

    // Keep only last 3 positions for calculations
    if (this.mouseLocs.length > 3) {
      this.mouseLocs.shift();
    }
  }

  private onTriggerEnter(): void {
    this.clearTimeout();
    this.activate();
  }

  private onTriggerLeave(): void {
    const delay = this.calculateDelay();

    if (delay > 0) {
      this.timeoutId = window.setTimeout(() => {
        this.deactivate();
      }, delay);
    } else {
      this.deactivate();
    }
  }

  private onMenuEnter(): void {
    this.clearTimeout();
    this.activate();
  }

  private onMenuLeave(): void {
    this.deactivate();
  }

  private activate(): void {
    if (!this.isActive) {
      this.isActive = true;
      this.options.activate();
    }
  }

  private deactivate(): void {
    if (this.isActive) {
      this.isActive = false;
      this.options.deactivate();
    }
  }

  private calculateDelay(): number {
    if (this.mouseLocs.length < 2) {
      return 0;
    }

    const currentLoc = this.mouseLocs[this.mouseLocs.length - 1];
    const prevLoc = this.mouseLocs[this.mouseLocs.length - 2];

    if (!currentLoc || !prevLoc) {
      return 0;
    }

    // Get menu bounds
    const menuRect = this.options.menu.getBoundingClientRect();
    const triggerRect = this.options.trigger.getBoundingClientRect();

    // Determine if mouse is moving toward the menu
    const isMovingTowardMenu = this.isMovingTowardTarget(prevLoc, currentLoc, menuRect, triggerRect);

    return isMovingTowardMenu ? this.options.delay! : 0;
  }

  private isMovingTowardTarget(prevLoc: MousePosition, currentLoc: MousePosition, menuRect: DOMRect, triggerRect: DOMRect): boolean {
    // Calculate vectors
    const mouseVector = {
      x: currentLoc.x - prevLoc.x,
      y: currentLoc.y - prevLoc.y,
    };

    // Find closest point on menu to current mouse position
    const closestMenuPoint = this.getClosestPointOnRect(currentLoc, menuRect);

    // Vector from mouse to menu
    const toMenuVector = {
      x: closestMenuPoint.x - currentLoc.x,
      y: closestMenuPoint.y - currentLoc.y,
    };

    // Check if mouse is moving toward menu using dot product
    const dotProduct = mouseVector.x * toMenuVector.x + mouseVector.y * toMenuVector.y;

    // Also check if mouse is within tolerance zone
    const tolerance = this.options.tolerance!;
    const isInToleranceZone =
      currentLoc.x >= Math.min(triggerRect.left, menuRect.left) - tolerance &&
      currentLoc.x <= Math.max(triggerRect.right, menuRect.right) + tolerance &&
      currentLoc.y >= Math.min(triggerRect.top, menuRect.top) - tolerance &&
      currentLoc.y <= Math.max(triggerRect.bottom, menuRect.bottom) + tolerance;

    return dotProduct > 0 && isInToleranceZone;
  }

  private getClosestPointOnRect(point: MousePosition, rect: DOMRect): MousePosition {
    return {
      x: Math.max(rect.left, Math.min(point.x, rect.right)),
      y: Math.max(rect.top, Math.min(point.y, rect.bottom)),
    };
  }

  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  public destroy(): void {
    this.clearTimeout();

    // Remove all event listeners
    document.removeEventListener('mousemove', this.boundMouseMove);
    this.options.trigger.removeEventListener('mouseenter', this.boundTriggerEnter);
    this.options.trigger.removeEventListener('mouseleave', this.boundTriggerLeave);
    this.options.menu.removeEventListener('mouseenter', this.boundMenuEnter);
    this.options.menu.removeEventListener('mouseleave', this.boundMenuLeave);
  }
}

export { SimpleMenuAim as MenuAim, type SimpleMenuAimOptions };
